<template>
  <div class="vuse-content-wrapper">
    <div v-if="hasAccess('timeClock')">
    <vuse-section-definition
      v-show="!hideTitle"
      :title="page"
      icon="mdi-timer"
    ></vuse-section-definition>

    <v-container fluid class="">
      <v-row>
        <v-col cols="12" lg="3" md="3" sm="4" class="d-flex">
          <v-text-field
            v-model="filter.SearchTerm"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            solo
          ></v-text-field
        ></v-col>

        <v-col cols="12" lg="3" md="3" sm="4">
          <TagsSelect
            v-model="filter.tags"
            label="Filter by Tags"
            hide-details
            solo
            clearable
            multiple
            dense
          />
        </v-col>

        <v-col cols="12" lg="3" md="3" sm="4">
          <TaskStatusSelect
            v-model="filter.status"
            label="Filter by Status"
            hide-details
            solo
            clearable
            dense
          />
        </v-col>
        <v-col cols="12" lg="3" md="3" sm="4">
          <v-select
            :return-object="false"
            :items="isTimeClockRunningOptions"
            v-model="filter.isTimeClockRunning"
            clearable
            solo
            dense
            hide-details
            item-text="text"
            item-value="value"
            label="Filter by TimeClock Running"
          />
        </v-col>

        <v-col class="text-right">
          <v-btn
            color="info"
            dark
            class="mb-2"
            @click="onAdd()"
            v-show="hasAccess('timeClock.add')"
          >
            Add Job
          </v-btn>
        </v-col>
      </v-row>

      <br />

      <template>
        <v-card>
          <v-skeleton-loader
            v-if="isLoading"
            class="mx-auto"
            type="table"
          ></v-skeleton-loader>
          <v-data-table
            v-else
            :headers="activeHeaders"
            :items="filteredDataItems"
            class="elevation-1"
            :options.sync="tableOptions"
            :server-items-length="totalItems"
            :loading="loading"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, 100, 200],
            }"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-menu offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on }">
                    <v-btn rounded class="content-ma-0 mr-2" v-on="on" x-small>
                      <v-icon small>menu</v-icon> Customize Columns
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item dense :key="i" v-for="(header, i) in headers">
                      <v-list-item-title v-if="header.readonly">
                        <v-checkbox
                          v-model="headers[i].active"
                          :label="header.text"
                          :disabled="true"
                        />
                      </v-list-item-title>
                      <v-list-item-title v-else>
                        <v-checkbox
                          v-model="headers[i].active"
                          :label="header.text"
                        />
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-spacer />

                <v-checkbox
                  v-model="filter.hideCompleted"
                  label="Hide Completed"
                  hide-details
                  dense
                  solo
                />
              </v-toolbar>
            </template>

            <template v-slot:item.dateCreated="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip @click="onEdit(item)" small v-bind="attrs" v-on="on">
                    {{ item.dateCreated | formatDatetime }}
                  </v-chip>
                </template>
                <span>{{ momentLocal(item.dateCreated).fromNow() }}</span>
              </v-tooltip>
            </template>

            <template v-slot:item.lastUpdated="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip @click="onEdit(item)" small v-bind="attrs" v-on="on">
                    {{ item.lastUpdated | formatDatetime }}
                  </v-chip>
                </template>
                <span>{{ momentLocal(item.lastUpdated).fromNow() }}</span>
              </v-tooltip>
            </template>

            <template v-slot:item.time="{ item }">
              <span @click="onTimeEntryForm(item)">
                <TimeclockClocking :item="item" />
              </span>
            </template>

            <template v-slot:item.date="{ item }">
              <v-chip @click="onEdit(item)" small>
                {{ item.date | formatDate }}
              </v-chip>
            </template>

            <template v-slot:item.dateCompleted="{ item }">
              <v-chip @click="onEdit(item)" small>
                {{ getDateCompleted(item) }}
              </v-chip>
            </template>

            <template v-slot:item.description="{ item }">
              <DescriptionDialog :text="item.description" />
            </template>

            <template v-slot:item.userId="{ item }">
              <UsersChip :value="item.taskUsers" />
            </template>

            <template v-slot:item.name="{ item }">
              <div
                style="
                  width: 100%;
                  max-height: 60px;
                  white-space: pre;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  margin: 10px;
                "
                @click="onEdit(item)"
                :title="item.name"
              >{{ item.name }}</div>
            </template>

            <template v-slot:item.client="{ item }">
              <ClientChip
                :clientId="item.clientId"
                :clientName="item.clientCompanyName"
              />
            </template>
            <template v-slot:item.isTimeClockRunning="{ item }">
              <v-btn-toggle borderless background-color="transparent">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="content-ma-0 mr-2" v-on="on">
                      <v-icon>mdi-cogs</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      @click="onCancel(item)"
                      v-if="hasAccess('timeClock.delete') && checkCancelable(item)"
                    >
                      <v-list-item-title>
                        <v-icon small left>cancel</v-icon>Cancel Time Clock
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="onEdit(item)"
                      v-if="hasAccess('timeClock.edit')"
                    >
                      <v-list-item-title>
                        <v-icon small left>mdi-pencil</v-icon>Edit
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="onTimeEntryForm(item)"
                      v-if="account.isAdmin"
                    >
                      <v-list-item-title>
                        <v-icon small left>timer</v-icon>Edit Time
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="onDelete(item)"
                      v-if="hasAccess('timeClock.edit')"
                    >
                      <v-list-item-title>
                        <v-icon small left>delete</v-icon>Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <TimeclockAction
                  :parentItem="item"
                  v-if="
                    hasAccess('timeClock.start') ||
                    hasAccess('timeClock.stop')
                  "
                  :showComplete="false"
                />
              </v-btn-toggle>
            </template>
            <template v-slot:item.status="{ item }">
              <TaskStatusChip :status="item.status" />
            </template>
          </v-data-table>
        </v-card>
      </template>
    </v-container>

    <TimeClockForm
      :show="showForm"
      :item="selectedItem"
      :client-id="clientId"
      @onClosed="clearForm()"
      @onSucceded="refresh()"
    />

    <TimeClockEntryForm
      :show="showTimeEntryForm"
      :item="selectedItem"
      :client-id="clientId"
      @onClosed="clearTimeEntryForm()"
    />

    <TimeClockDelete
      :show="showDelete"
      :item="selectedItem"
      @onClosed="clearDelete()"
      @onSucceded="refresh()"
    />

    <TimeClockCancel
      :show="showCancel"
      :item="selectedItem"
      @onClosed="clearCancel()"
      @onSucceded="refresh()"
    />
  </div>
  <NoPermission v-else />
  </div>
</template>

<script>
import TaskStatusChip from "components/TaskStatusChip";
import TaskStatusSelect from "components/TaskStatusSelect";
import { mapActions, mapState, mapGetters } from "vuex";
import TimeClockForm from "./Task/_FormJob";
import TimeClockDelete from "./Timeclock/_Delete";
import TimeClockCancel from "./Timeclock/_Cancel";
import TimeclockAction from "./Timeclock/__TimeclockAction";
import TimeclockClocking from "./Timeclock/__TimeclockClocking";
import TimeClockEntryForm from "./_TimeClockEntry";
import TagsSelect from "components/TagSelect.vue";
import ClientChip from "components/ClientChip";
import formatterMixin from "@/mixins/formatterMixin";
import UsersChip from "components/UsersChip";
import { getStorage, setStorage } from "src/utils/helpers";
import _ from "lodash";
import DescriptionDialog from "components/DescriptionDialog";
import NoPermission from "components/NoPermission";

const SETTING_LIST_KEY = "timeClock-columns";
const defaultHeaders = [
  {
    text: "Action",
    value: "isTimeClockRunning",
    sortable: true,
    active: true,
    readonly: true,
  },
  { text: "Job Number", value: "code", sortable: true, active: true },
  { text: "Name", value: "name", sortable: true, active: true },
  {
    text: "Clients",
    align: "start",
    sortable: false,
    value: "client",
    active: false,
  },
  { text: "Assigned To", value: "userId", sortable: false, active: false },
  { text: "Status", value: "status", sortable: true, active: false },
  {
    text: "Description",
    value: "description",
    sortable: true,
    active: true,
  },
  { text: "Time", value: "time", sortable: false, active: false },
  { text: "Date", value: "date", sortable: true, active: false },
  {
    text: "Date Completed",
    value: "dateCompleted",
    sortable: true,
    active: false,
  },
  {
    text: "Date Created",
    value: "dateCreated",
    sortable: true,
    active: false,
  },
  {
    text: "Date Modified",
    value: "lastUpdated",
    sortable: true,
    active: false,
  },
];

export default {
  mixins: [formatterMixin],
  props: {
    clientId: Number,
    hideTitle: Boolean,
  },
  components: {
    UsersChip,
    ClientChip,
    TimeclockClocking,
    TimeClockForm,
    TimeclockAction,
    TaskStatusChip,
    TimeClockDelete,
    TimeClockCancel,
    TimeClockEntryForm,
    TagsSelect,
    TaskStatusSelect,
    DescriptionDialog,
    NoPermission
  },
  data: () => ({
    showTimeEntryForm: false,
    showForm: false,
    showDelete: false,
    showCancel: false,
    selectedItem: {},
    namespace: "Time Clock",
    page: "Time Clock",
    usage: "usage",
    timer: null,
    tableOptions: {},
    isTimeClockRunningOptions: [
      { text: "Show All", value: null },
      { text: "Show Running TimeClock", value: true },
      { text: "Show Not Running TimeClock", value: false },
    ],

    filter: {
      SearchTerm: "",
      clientId: "",
      status: "available",
      isTimeClockRunning: null,
      hideCompleted: null,
      tags: [],
    },
    headers: defaultHeaders.map((item) => {
      let savedHeaders = getStorage(SETTING_LIST_KEY) || [];
      let nowHeader = savedHeaders.filter(
        (saved) => saved.value === item.value
      );

      if (nowHeader) {
        nowHeader = nowHeader[0];
      }
      return {
        ...item,
        ...nowHeader,
      };
    }),
  }),
  computed: {
    ...mapGetters("account", ["hasAccess"]),
    ...mapState({
      account: (state) => state.account.data,
      loading: (state) => state.timeclock.loadingTable,
      totalItems: (state) => state.timeclock.metaData.totalEntries,
    }),

    isLoading() {
      return this.loading && this.dataItems.length == 0;
    },

    computedFilter() {
      const {
        clientId,
        tags,
        status,
        isTimeClockRunning,
        SearchTerm,
        hideCompleted,
      } = this.filter;
      let { sortBy, sortDesc, page, itemsPerPage } = this.tableOptions;
      let params = {
        PageNumber: page ? page : 1,
        PageSize: itemsPerPage > 0 ? itemsPerPage : 10,
      };

      if(sortBy.length > 0 && sortBy[0]) {
        params.OrderBy = sortBy[0];
        if (sortDesc.length > 0) {
          params.Ascending = !sortDesc[0];
        } else {
          params.Ascending = false;
        }
      }

      if (isTimeClockRunning !== null) {
        params.isTimeClockRunning = isTimeClockRunning;
      }

      if (status) {
        params.status = status;
      }

      if (clientId) {
        params.clientId = clientId;
      }

      if (SearchTerm) {
        params.SearchTerm = SearchTerm;
      }

      if (hideCompleted) {
        params.hideCompleted = true;
      }

      if (tags && tags.length > 0) {
        params.tagsArray = [];
        tags.map((tag) => {
          params.tagsArray.push(tag);
        });
      }

      return params;
    },

    tags() {
      return this.filter.tags;
    },
    activeHeaders() {
      return this.headers.filter((header) => header.active);
    },

    filteredDataItems() {
      let dataItems = [...this.dataItems];

      if (!this.hasAccess('timeClock.view')) {        
        dataItems = dataItems.filter(item => {
          let userIds = item.taskUsers.map(user => user.userId);
          return userIds.includes(this.account.user.id);
        });      
      }
      return dataItems;
    },

    dataItems() {
      return this.$store.getters["timeclock/getAll"];
    },
  },
  watch: {
    clientId(n) {
      if (n) {
        this.filter.clientId = n;
      }
    },
    headers: {
      handler() {
        setStorage(SETTING_LIST_KEY, this.headers);
      },
      deep: true,
    },

    tableOptions: {
      handler(n, o) {
        if (JSON.stringify(n) !== JSON.stringify(o)) {
          this.refresh();
        }
      },
      deep: true,
    },

    filter: {
      async handler() {
        await this.refresh(true);
      },
      deep: true,
    },
  },

  async mounted() {
    if (this.clientId) {
      this.filter.clientId = this.clientId;
    }
    await this.refresh();

    this.timer = setInterval(() => {
      this.refreshNoLoading();
    }, (60 * 1000));
  },

  beforeDestroy() {
    if (this.clientId) {
      this.clearTableFilter();
    }
    clearInterval(this.timer);
    this.$store.dispatch("timeclock/clearData");
  },

  methods: {
    ...mapActions("timeclock", [
      "getAll",
      "clearTableFilter",
      "applyTableFilter",
    ]),

    getDateCompleted(item) {
      let dateCompleted = null;
      if (item.isCompleted == true || item.status == "completed") {
        dateCompleted = item.dateCompleted
          ? item.dateCompleted
          : item.lastUpdated;
      } else {
        dateCompleted = null;
      }
      return this.formatDate(dateCompleted);
    },

    checkCancelable(item) {
      return !!item?.runningStartTime;
    },

    refresh: _.debounce(async function () {
      this.refreshTable(true);
    }, 500),

    async refreshTable(noLoading = true) {
      this.applyTableFilter({ ...this.computedFilter, noLoading });
    },

    async refreshNoLoading() {
      if (!this.loading) {
        await this.refreshTable(false);
      }
    },

    onTimeEntryForm(item) {
      this.showTimeEntryForm = true;
      this.selectedItem = item;
    },

    async clearTimeEntryForm() {
      this.showTimeEntryForm = false;
      this.selectedItem = {};
      await this.refresh();
    },

    onCancel(item) {
      this.selectedItem = item;
      this.showCancel = true;
    },

    onEdit(item) {
      if (!this.hasAccess("timeClock.edit")) {
        return false;
      }
      this.showForm = true;
      this.selectedItem = item;
    },

    onDelete(item) {
      this.showDelete = true;
      this.selectedItem = item;
    },

    onAdd() {
      this.showForm = true;
      this.selectedItem = {};
    },

    clearForm() {
      this.showForm = false;
      this.selectedItem = {};
    },

    clearCancel() {
      this.showCancel = false;
      this.selectedItem = {};
    },

    clearDelete() {
      this.showDelete = false;
      this.selectedItem = {};
    },
  },
};
</script>
